/**
 * @file src/pages/device-settings/components/ComplianceCertificatesTab.jsx
 * @description Second tab of Device Settings — list of devices with their
 * compliance certificate status and a "Generate" button per device.
 *
 * Flow:
 *  1. User clicks Generate on a device → opens OTP input modal
 *  2. User enters OTP from ZATCA Fatoora Portal → submits
 *  3. generateComplianceCSR mutation runs
 *  4. On success → invalidate devices query → kick off generateProductionCSR
 *     (production CSR is chained because compliance + production are usually
 *     done back-to-back during onboarding)
 *
 * Note on the chained production CSR:
 *  This was the pre-existing behavior. If production-CSR generation fails,
 *  it shows its own toast but does NOT roll back the compliance step
 *  (compliance succeeded — that part is durable).
 */

import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import StatusBadge from "./StatusBadge";
import OTPInputModal from "./OTPInputModal";
import { useGenerateComplianceCSR } from "../useGenerateComplianceCSR";
import { useGenerateProductionCSR } from "../useGenerateProductionCSR";
import { QUERY_KEYS } from "config/queryKeys";

const ComplianceCertificatesTab = ({ devices }) => {
  const queryClient = useQueryClient();
  const { mutate: generateComplianceCSR } = useGenerateComplianceCSR();
  const { mutate: generateProductionCSR } = useGenerateProductionCSR();

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isGeneratingCert, setIsGeneratingCert] = useState(false);
  const [isOTPModalOpen, setIsOTPModalOpen] = useState(false);

  // ── Open the OTP modal ──────────────────────────────────────────────────
  const handleGenerateCompliance = (device) => {
    setSelectedDevice(device);
    setIsOTPModalOpen(true);
  };

  // ── OTP submitted → run mutations ───────────────────────────────────────
  const handleOTPSubmit = (otp) => {
    setIsGeneratingCert(true);

    generateComplianceCSR(
      { deviceId: selectedDevice.deviceId, otp },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.devices.all });

          // Chain production CSR right after compliance
          generateProductionCSR(selectedDevice.deviceId, {
            onError: (error) => {
              toast.error(
                "Production CSR generation failed: " + error.message,
                { id: "production-csr-error" },
              );
            },
          });

          resetState();
        },
        onError: (error) => {
          const isAuthError = error?.response?.message?.includes("logged in");
          if (isAuthError) {
            toast.error("you must be logged in to access this resource", {
              id: "auth-error",
            });
          } else {
            toast.error(error.message);
          }
          resetState();
        },
      },
    );
  };

  const resetState = () => {
    setIsGeneratingCert(false);
    setIsOTPModalOpen(false);
    setSelectedDevice(null);
  };

  const handleOTPModalClose = () => {
    if (!isGeneratingCert) {
      setIsOTPModalOpen(false);
      setSelectedDevice(null);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-foreground">
            Compliance Certificates
          </h2>
          <span className="text-sm text-muted-foreground">
            Manage ZATCA compliance certificates
          </span>
        </div>

        <div className="space-y-4">
          {devices.map((device) => (
            <div
              key={device.deviceId}
              className="bg-muted/30 border border-border rounded-xl p-6"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <Icon name="Shield" size={16} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">
                      Device {device.deviceSerialNumber}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {device.organizationName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <StatusBadge status={device.certificates.compliance.status} />
                  <Button
                    onClick={() => handleGenerateCompliance(device)}
                    disabled={isGeneratingCert}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {isGeneratingCert ? (
                      <Icon name="Loader" size={16} className="animate-spin" />
                    ) : (
                      <Icon name="Generated" size={16} />
                    )}
                    <span className="ml-2">
                      {device.certificates.compliance.status === "active"
                        ? "Generated"
                        : "Generate"}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <OTPInputModal
        isOpen={isOTPModalOpen}
        onClose={handleOTPModalClose}
        onSubmit={handleOTPSubmit}
        isLoading={isGeneratingCert}
        deviceInfo={selectedDevice}
      />
    </>
  );
};

export default ComplianceCertificatesTab;
